import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'

import { PubSubService, getStripePromise, ErrorTrackingAPI } from '@services'
import { Icon } from '@components'
import { useCashRegister, usePubSubEvents } from '@hooks'
import { customToast, PUBSUB_GROUP_EVENT, /* PUB_SUB_EVENT, */ ROUTES } from '@common'
import { storageHelpers } from '@utils'

const PAYMENT_TIMEOUT = 120 * 1000
const POLLING_INTERVAL = 2000
const POLLING_START_DELAY = 0

const INSTRUCTIONS = [
    'Save this QR code to your photos or take a screenshot',
    'Open your bank app or payment app',
    'Select the option to scan a QR code',
    'Upload the QR code you saved earlier',
    'Complete the transaction',
]

const PaymentScreen = () => {
    const { clientSecret, orderId } = useParams()
    const navigate = useNavigate()
    const { state } = useLocation()
    const qrCodeSVG = state?.qrCodeSVG
    const hostedVoucherUrl = state?.hostedVoucherUrl
    const { joinedGroupName } = useCashRegister()

    const paymentTimer = useRef(null)
    const pollingInterval = useRef(null)
    const pollingStartTimeout = useRef(null)
    const isPaymentCompleted = useRef(false)
    const isPaymentCancelled = useRef(false)
    const isPaymentFailedSent = useRef(false)
    const isNavigatingRef = useRef(false) // Prevent multiple navigation calls
    const [isQrCodeLoaded, setIsQrCodeLoaded] = useState(false)
    const [isCopied, setIsCopied] = useState(false)

    // Handle navigation back - navigate to OrderCompletePage or history based on paymentOrigin
    const handleNavigateBack = useCallback(() => {
        // Guard: Prevent multiple navigation calls
        if (isNavigatingRef.current) {
            return
        }

        // Set navigation flag to prevent duplicate calls
        isNavigatingRef.current = true

        const paymentOrigin = storageHelpers.getPaymentOrigin()

        if (paymentOrigin === 'checkout-assistant') {
            // Use React Router navigate instead of window.history.go(-1) to ensure proper component unmounting
            // This prevents Stripe.js from trying to access DOM during navigation
            navigate(-1)
        } else if (paymentOrigin === 'advance-takeaway') {
            // If order is advance takeaway, navigate to member history with upcoming tab
            navigate(`${ROUTES.Order.history}?tab=upcoming`, { replace: false })
        } else if (orderId) {
            // Navigate back to OrderCompletePage for regular orders
            navigate(ROUTES.Order.complete.replace(':orderId', orderId), { replace: false })
        } else {
            navigate(-1)
        }
    }, [orderId, navigate])

    // Cleanup polling interval
    const cleanupPolling = useCallback(() => {
        if (pollingInterval.current) {
            clearInterval(pollingInterval.current)
            pollingInterval.current = null
        }
    }, [])

    const cleanupPollingStartDelay = useCallback(() => {
        if (pollingStartTimeout.current) {
            clearTimeout(pollingStartTimeout.current)
            pollingStartTimeout.current = null
        }
    }, [])

    // Send PAYMENT_FAILED event if conditions are met
    const sendPaymentFailedIfNeeded = useCallback(async () => {
        const paymentOrigin = storageHelpers.getPaymentOrigin()
        if (
            paymentOrigin === 'checkout-assistant' &&
            !isPaymentCompleted.current &&
            !isPaymentCancelled.current &&
            !isPaymentFailedSent.current
        ) {
            isPaymentFailedSent.current = true
            storageHelpers.clearCheckoutAssistantPaymentSuccess()
            await PubSubService.sendToGroup(joinedGroupName, { type: PUBSUB_GROUP_EVENT.PAYMENT_FAILED })
        }
    }, [joinedGroupName])

    const pubSubEvents = useMemo(() => [PUBSUB_GROUP_EVENT.PAYMENT_CANCEL], [])

    const pubSubHandlers = useMemo(
        () => ({
            [PUBSUB_GROUP_EVENT.PAYMENT_CANCEL]: () => {
                // Prevent duplicate processing - check flag at the start
                if (isPaymentCancelled.current) {
                    return
                }

                const paymentOrigin = storageHelpers.getPaymentOrigin()

                if (paymentOrigin === 'checkout-assistant') {
                    // Set flag BEFORE processing to prevent duplicate handling
                    isPaymentCancelled.current = true

                    // Set cancel flag in localStorage so CheckoutAssistantPage can display the modal
                    storageHelpers.setCheckoutAssistantPaymentCancel({
                        timestamp: Date.now(),
                    })

                    // Clear the payment success flag
                    storageHelpers.clearCheckoutAssistantPaymentSuccess()

                    // Clean up polling before navigation
                    cleanupPolling()
                    cleanupPollingStartDelay()

                    requestAnimationFrame(() => {
                        handleNavigateBack()
                    })
                } else {
                    // Set flag for non-checkout-assistant cases too
                    isPaymentCancelled.current = true
                    cleanupPolling()
                    cleanupPollingStartDelay()
                    handleNavigateBack()
                }
            },
            // TODO: Re-enable after monitoring PubSub reliability in production
            // [PUB_SUB_EVENT.ORDER_PAYMENT_STATUS_UPDATE]: payload => {
            //     if (isPaymentCompleted.current || isPaymentCancelled.current || isNavigatingRef.current) {
            //         return
            //     }
            //
            //     if (!payload?.orderId || payload.orderId !== orderId) {
            //         return
            //     }
            //
            //     const paymentIntentId = payload?.payment?.paymentIntentId
            //     const paymentClientSecret = payload?.payment?.clientSecret || clientSecret
            //
            //     if (!paymentIntentId || !paymentClientSecret) {
            //         return
            //     }
            //
            //     cleanupPolling()
            //     cleanupPollingStartDelay()
            //     isPaymentCompleted.current = true
            //
            //     if (paymentTimer.current) {
            //         clearTimeout(paymentTimer.current)
            //         paymentTimer.current = null
            //     }
            //
            //     navigate(
            //         ROUTES.Payment.check
            //             .replace(':orderId', orderId)
            //             .replace(':paymentIntentId', paymentIntentId)
            //             .replace(':clientSecret', paymentClientSecret),
            //         { replace: true }
            //     )
            // },
        }),
        [cleanupPolling, cleanupPollingStartDelay, handleNavigateBack]
    )

    usePubSubEvents({
        events: pubSubEvents,
        handlers: pubSubHandlers,
    })

    // Handle popstate (user pressing back button)
    useEffect(() => {
        const preventNavigation = async () => {
            if (isNavigatingRef.current) return

            window.removeEventListener('popstate', preventNavigation)

            if (paymentTimer.current) {
                clearTimeout(paymentTimer.current)
                paymentTimer.current = null
            }

            cleanupPolling()
            cleanupPollingStartDelay()
            await sendPaymentFailedIfNeeded()
        }

        window.addEventListener('popstate', preventNavigation)

        return () => {
            window.removeEventListener('popstate', preventNavigation)
            cleanupPolling()
            cleanupPollingStartDelay()
        }
    }, [cleanupPolling, cleanupPollingStartDelay, sendPaymentFailedIfNeeded])

    // Timeout timer - auto navigate back after PAYMENT_TIMEOUT
    useEffect(() => {
        paymentTimer.current = setTimeout(async () => {
            if (isNavigatingRef.current) return

            if (paymentTimer.current) {
                clearTimeout(paymentTimer.current)
                paymentTimer.current = null
            }

            cleanupPolling()
            cleanupPollingStartDelay()
            await sendPaymentFailedIfNeeded()
            handleNavigateBack()
        }, PAYMENT_TIMEOUT)

        return () => {
            if (paymentTimer.current) {
                clearTimeout(paymentTimer.current)
                paymentTimer.current = null
            }
            cleanupPolling()
            cleanupPollingStartDelay()
        }
    }, [handleNavigateBack, cleanupPolling, cleanupPollingStartDelay, sendPaymentFailedIfNeeded])

    // Polling logic - check payment status every 2 seconds (after delay)
    useEffect(() => {
        if (!clientSecret) return

        const pollPaymentStatus = async () => {
            // Don't poll if already completed or cancelled
            if (isPaymentCompleted.current || isPaymentCancelled.current || isNavigatingRef.current) {
                cleanupPolling()
                return
            }

            try {
                const stripe = await getStripePromise()
                if (!stripe) return

                const { paymentIntent, error } = await stripe.retrievePaymentIntent(clientSecret)

                if (error) {
                    ErrorTrackingAPI.sendError(error, 'PaymentScreen', 'pollPaymentStatus')
                    return
                }

                if (paymentIntent?.status === 'succeeded') {
                    // Payment successful
                    cleanupPolling()
                    cleanupPollingStartDelay()
                    isPaymentCompleted.current = true

                    // Clear timeout
                    if (paymentTimer.current) {
                        clearTimeout(paymentTimer.current)
                        paymentTimer.current = null
                    }

                    // Navigate to CheckPaymentPage
                    navigate(
                        ROUTES.Payment.check
                            .replace(':orderId', orderId)
                            .replace(':paymentIntentId', paymentIntent.id)
                            .replace(':clientSecret', clientSecret),
                        { replace: true }
                    )
                } else if (['canceled', 'requires_payment_method'].includes(paymentIntent?.status)) {
                    // Payment was canceled
                    cleanupPolling()
                    cleanupPollingStartDelay()

                    if (paymentTimer.current) {
                        clearTimeout(paymentTimer.current)
                        paymentTimer.current = null
                    }

                    const paymentOrigin = storageHelpers.getPaymentOrigin()
                    if (paymentOrigin !== 'checkout-assistant') {
                        customToast.error('Payment was cancelled or failed. Please try again.')
                    }

                    await sendPaymentFailedIfNeeded()
                    handleNavigateBack()
                }
            } catch (err) {
                ErrorTrackingAPI.sendError(err, 'PaymentScreen', 'pollPaymentStatus')
            }
        }

        const startPolling = () => {
            if (pollingInterval.current || isPaymentCompleted.current || isPaymentCancelled.current || isNavigatingRef.current) {
                return
            }

            pollingInterval.current = setInterval(pollPaymentStatus, POLLING_INTERVAL)
            pollPaymentStatus()
        }

        pollingStartTimeout.current = setTimeout(startPolling, POLLING_START_DELAY)

        return () => {
            cleanupPolling()
            cleanupPollingStartDelay()
        }
    }, [clientSecret, orderId, navigate, cleanupPolling, cleanupPollingStartDelay, sendPaymentFailedIfNeeded, handleNavigateBack])

    const handleQrCodeLoad = useCallback(() => {
        setIsQrCodeLoaded(true)
        // window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })
    }, [])

    const handleCopyLink = useCallback(() => {
        if (!hostedVoucherUrl) return
        navigator.clipboard
            .writeText(hostedVoucherUrl)
            .then(() => {
                setIsCopied(true)
                setTimeout(() => setIsCopied(false), 2000)
            })
            .catch(() => {})
    }, [hostedVoucherUrl])

    const handleClose = useCallback(async () => {
        if (paymentTimer.current) {
            clearTimeout(paymentTimer.current)
            paymentTimer.current = null
        }
        cleanupPolling()
        cleanupPollingStartDelay()
        await sendPaymentFailedIfNeeded()
        handleNavigateBack()
    }, [cleanupPolling, cleanupPollingStartDelay, sendPaymentFailedIfNeeded, handleNavigateBack])

    // If no QR code URL, show error message
    if (!qrCodeSVG) {
        return (
            <div className="payment-screen">
                <div className="payment-screen__content">
                    <h2 className="payment-screen__title">Unable to load QR Code</h2>
                    <p className="payment-screen__description">
                        The PayNow QR code could not be generated. Please go back and try again.
                    </p>
                    <button className="btn btn-primary mt-3" onClick={() => handleNavigateBack()}>
                        Go Back
                    </button>
                </div>
            </div>
        )
    }

    return (
        <div className="payment-screen">
            <div className="payment-screen__content">
                {/* Title */}
                <h2 className="payment-screen__title">
                    Scan with your bank app
                    <br />
                    or payment app
                </h2>

                {/* QR Code */}
                <div className="payment-screen__qr-wrapper">
                    {!isQrCodeLoaded && <div className="payment-screen__qr-placeholder" />}
                    <img
                        src={qrCodeSVG}
                        alt="PayNow QR Code"
                        className="payment-screen__qr-image"
                        onLoad={handleQrCodeLoad}
                        style={{ display: isQrCodeLoaded ? 'block' : 'none' }}
                    />
                    <Icon.PayNowLogo className="payment-screen__paynow-icon" />
                </div>

                {/* Description */}
                <p className="payment-screen__description">
                    Payments collected via Stripe. Payee name may appear as &ldquo;Stripe Payments Singapore Pte. Ltd.&rdquo;.
                    PayNow is supported by bank and payment apps such as
                </p>

                {/* Bank icons */}
                <div className="payment-screen__bank-icons">
                    <Icon.DBSPayLahIcon />
                    <Icon.GrabPayIcon />
                    <Icon.OCBCIcon />
                    <Icon.UOBIcon />
                </div>

                {/* Step-by-step instructions */}
                <div className="payment-screen__steps">
                    {INSTRUCTIONS.map((text, index) => (
                        <div className="payment-screen__step" key={index}>
                            <div className="payment-screen__stepper">
                                <div className="payment-screen__stepper-circle" />
                                {index < INSTRUCTIONS.length - 1 && <div className="payment-screen__stepper-line" />}
                            </div>
                            <span className="payment-screen__step-text">{text}</span>
                        </div>
                    ))}
                </div>

                {/* Copy link button */}
                {hostedVoucherUrl && (
                    <button className="payment-screen__copy-btn" onClick={handleCopyLink}>
                        <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                            <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                        </svg>
                        {isCopied ? 'Copied!' : 'Copy link for sharing'}
                    </button>
                )}
            </div>
        </div>
    )
}

export default PaymentScreen
